import type { TearsheetCustomBox, TearsheetLayoutItem } from '@hf/sharedContract';
import { TEARSHEET_ADDABLE_SECTION_IDS, TEARSHEET_SECTION_IDS } from '@hf/sharedContract';

export const NOT_IMPLEMENTED = 'Not implemented yet';

export const STOCK_LAYOUT_OWNER = 'Stock';
/** Sentinel createdBy for layouts owned by the current session before auth identity is shown. */
export const MY_LAYOUT_CREATED_BY = 'local';

/** True when the layout is a custom layout owned by the session tenant (not stock / foreign). */
export function layoutOwnedByTenant(layout: { isStock?: boolean; clientId?: string }, tenantId: string | undefined): boolean {
  if (layout.isStock) return false;
  const tenant = tenantId?.trim();
  const clientId = layout.clientId?.trim();
  if (!tenant || !clientId) return false;
  return clientId.toLowerCase() === tenant.toLowerCase();
}

export function layoutOwnerCaption(layout: { isStock?: boolean; createdBy?: string }): string {
  if (layout.isStock) return STOCK_LAYOUT_OWNER;
  return layout.createdBy?.trim() || '';
}

export function copyLayoutName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'Layout copy';
  return / copy$/i.test(trimmed) ? trimmed : `${trimmed} copy`;
}

export function customLayoutDisplayName(layoutName: string, stockNames: Iterable<string>): string {
  const stock = stockNames instanceof Set ? stockNames : new Set(stockNames);
  return stock.has(layoutName) ? copyLayoutName(layoutName) : layoutName;
}

export const SECTION_LABELS: Record<string, string> = {
  header: 'Report header',
  kpis: 'Key figures',
  commentary: 'Program Description',
  monthly: 'Monthly Returns',
  vami: 'VAMI',
  risk: 'Performance Statistics',
  sector_attribution: 'Sector Attribution (last 12 months)',
  exposure_attribution: 'Current Exposure',
  last_month_attribution: 'Attribution',
  top_exposure: 'Exposure Top 5',
  bottom_exposure: 'Exposure Bottom 5',
  top_gross_performance: 'Gross Performance Top 5',
  bottom_gross_performance: 'Gross Performance Bottom 5',
  volatility_contribution: 'Volatility Contribution',
  volatility_instruments: 'Volatility Top 10 Instruments',
  drawdowns: 'Drawdown Table',
  contact: 'Contact Information',
  subscription: 'Subscription Information',
  disclaimer: 'Disclaimer',
  portfolio_manager: 'Portfolio Manager',
  predefined_text: 'Header Text',
  rolling_summary: 'Rolling Summary',
  largest_drawdowns: 'Largest Drawdowns',
  annual_returns: 'Annual Returns',
  correlation_to_indices: 'Correlation To Indices',
  tier_item_properties: 'Tier Item Properties',
  cumulative_return: 'Cumulative Return (%)',
  sector_risk_alloc_var: 'Sector Risk Alloc (Undiversified VaR)',
  initial_margin_equity: 'Initial Margin to Equity Ratio',
  maintenance_margin_equity: 'Maintenance Margin to Equity Ratio',
  monthly_return_and_drawdown: 'Monthly Return and Drawdown',
  rolling_return: 'Rolling Return',
  rolling_volatility: 'Rolling Volatility',
  monthly_returns_distribution: 'Monthly Returns Distribution',
  monthly_returns_bar: 'Monthly Returns (Bar Chart)',
  monthly_drawdown_bar: 'Monthly Drawdown (Bar Chart)',
  vami_and_monthly_returns: 'VAMI and Monthly Returns',
  rolling_sharpe: 'Rolling Sharpe',
  rolling_volatility_annualized: 'Rolling Volatility Annualized',
  component_var_by_sector: 'Component VAR by Sector',
  performance_during_index_worst: 'Performance During 10 Worst Months of S&P 500 INDEX',
  sector_decomposition: 'Sector Performance Decomposition',
  risk_statistics: 'Risk Statistics',
  return_statistics: 'Return Statistics',
};

const CANONICAL_SET = new Set<string>(TEARSHEET_SECTION_IDS);

export const ADDABLE_SECTIONS: Array<{ value: string; label: string }> = [
  ...TEARSHEET_ADDABLE_SECTION_IDS.map((value) => ({
    value,
    label: SECTION_LABELS[value] ?? value,
  })),
  { value: 'custom', label: 'Free text / Custom box' },
];

/** Builder grid columns. Stored colSpan uses the contract values below (×3). */
export const GRID_COLUMNS = 4;

/** One CSS grid row track; section heights are fixed multiples of this (by type). */
export const GRID_ROW_PX = 56;

/** Must match `.hf-ts .grid` / `.grid--builder` `gap` in tearsheets.css. */
export const GRID_GAP_PX = 10;

/** Width the PDF render lays the report out at — must match `PRINT_LAYOUT_WIDTH_PX` in `generateReportPdf`. */
export const PRINT_LAYOUT_WIDTH_PX = 1280;

const PM_MIN_ROW_SPAN = 2;
const PM_MAX_ROW_SPAN = 48;

/** Portfolio manager grows with copy; other sections stay on the 1–4 track cap. */
export function clampPmRowSpan(rowSpan: number): number {
  if (!Number.isFinite(rowSpan)) return PM_MIN_ROW_SPAN;
  return Math.min(PM_MAX_ROW_SPAN, Math.max(PM_MIN_ROW_SPAN, Math.round(rowSpan)));
}

/** Grid row tracks needed so a PM card of `scrollHeight` is not clipped. */
export function pmRowSpanForHeight(scrollHeight: number): number {
  return clampPmRowSpan(Math.ceil((scrollHeight + GRID_GAP_PX) / (GRID_ROW_PX + GRID_GAP_PX)));
}

const TEXT_ROW_STEPS = [2, 4, 6, 8] as const;
const TEXT_ROW_MIN = TEXT_ROW_STEPS[0] ?? 2;
const TEXT_ROW_MAX = TEXT_ROW_STEPS[TEXT_ROW_STEPS.length - 1] ?? 8;

/** Running-text cards (custom box, program description, header text, subscription). The portfolio manager grows on its own scale. */
export function isTextCard(id: string): boolean {
  if (isCustomSection(id)) return true;
  const base = baseSectionId(id);
  return base === 'commentary' || base === 'predefined_text' || base === 'subscription';
}

/** Text cards are born at 2 rows and step to 4, 6, then 8. Never past 8. */
export function clampTextRowSpan(rowSpan: number): (typeof TEXT_ROW_STEPS)[number] {
  if (!Number.isFinite(rowSpan)) return TEXT_ROW_MIN;
  return TEXT_ROW_STEPS.find((step) => rowSpan <= step) ?? TEXT_ROW_MAX;
}

/** Smallest 2/4/6/8 step that fits `scrollHeight`, capped at 8. */
export function textRowSpanForHeight(scrollHeight: number): (typeof TEXT_ROW_STEPS)[number] {
  const tracks = Math.ceil((scrollHeight + GRID_GAP_PX) / (GRID_ROW_PX + GRID_GAP_PX));
  return clampTextRowSpan(tracks);
}

export function clampContentRowSpan(id: string, rowSpan: number): number {
  return isTextCard(id) ? clampTextRowSpan(rowSpan) : clampPmRowSpan(rowSpan);
}

export function contentRowSpanForHeight(id: string, scrollHeight: number): number {
  return isTextCard(id) ? textRowSpanForHeight(scrollHeight) : pmRowSpanForHeight(scrollHeight);
}

export const WIDTH_OPTIONS: Array<{ value: string; label: string; colSpan: TearsheetLayoutItem['colSpan'] }> = [
  { value: '3', label: '1/4', colSpan: 3 },
  { value: '6', label: '1/2', colSpan: 6 },
  { value: '9', label: '3/4', colSpan: 9 },
  { value: '12', label: 'Full', colSpan: 12 },
];

const ALL_COL_SPANS: ReadonlyArray<TearsheetLayoutItem['colSpan']> = WIDTH_OPTIONS.map((option) => option.colSpan);
const ALLOWED_COL_SPANS = new Set<TearsheetLayoutItem['colSpan']>(ALL_COL_SPANS);
const ALLOWED_ROW_SPANS = new Set([1, 2, 3, 4, 6] as const);

/**
 * Widths where a stencil is still readable on a 14" display, from rendering every stencil at each width.
 * Sections absent here (chrome, custom boxes) keep all four widths.
 */
const SECTION_COL_SPANS: Record<string, ReadonlyArray<TearsheetLayoutItem['colSpan']>> = {
  annual_returns: [3],
  largest_drawdowns: [3],
  risk: [3, 6, 9, 12],
  return_statistics: [3, 6, 9, 12],
  risk_statistics: [3, 6, 9, 12],
  sector_decomposition: [3, 6],
  subscription: [3, 6],
  tier_item_properties: [3, 6],
  last_month_attribution: [6],
  sector_risk_alloc_var: [6],
  volatility_contribution: [6],
  bottom_exposure: [6, 9, 12],
  bottom_gross_performance: [6, 9, 12],
  component_var_by_sector: [6, 9, 12],
  correlation_to_indices: [6, 9, 12],
  cumulative_return: [6, 9, 12],
  drawdowns: [6, 9, 12],
  exposure_attribution: [6, 9, 12],
  initial_margin_equity: [6, 9, 12],
  maintenance_margin_equity: [6, 9, 12],
  sector_attribution: [6, 9, 12],
  top_exposure: [6, 9, 12],
  top_gross_performance: [6, 9, 12],
  vami: [6, 9, 12],
  vami_and_monthly_returns: [6, 9, 12],
  monthly: [9, 12],
  rolling_return: [9, 12],
  rolling_sharpe: [9, 12],
  rolling_summary: [9, 12],
  commentary: [12],
  portfolio_manager: [12],
};

export function allowedColSpans(id: string): ReadonlyArray<TearsheetLayoutItem['colSpan']> {
  return SECTION_COL_SPANS[baseSectionId(id)] ?? ALL_COL_SPANS;
}

type RowSpanUnits = 1 | 2 | 3 | 4 | 6;

/** Fixed row multipliers per section type. Most cards are 1–4 tracks; VAMI is 6. */
const SECTION_ROW_SPANS: Record<string, RowSpanUnits> = {
  header: 2,
  kpis: 2,
  commentary: 2,
  monthly: 4,
  vami: 6,
  risk: 4,
  sector_attribution: 4,
  exposure_attribution: 4,
  last_month_attribution: 4,
  top_exposure: 3,
  bottom_exposure: 3,
  top_gross_performance: 3,
  bottom_gross_performance: 3,
  volatility_contribution: 3,
  volatility_instruments: 4,
  drawdowns: 4,
  contact: 2,
  subscription: 2,
  disclaimer: 1,
  portfolio_manager: 2,
  predefined_text: 2,
  rolling_summary: 4,
  largest_drawdowns: 4,
  annual_returns: 2,
  correlation_to_indices: 4,
  tier_item_properties: 2,
  cumulative_return: 4,
  sector_risk_alloc_var: 4,
  initial_margin_equity: 4,
  maintenance_margin_equity: 4,
  monthly_return_and_drawdown: 4,
  rolling_return: 4,
  rolling_volatility: 4,
  monthly_returns_distribution: 4,
  monthly_returns_bar: 4,
  monthly_drawdown_bar: 4,
  vami_and_monthly_returns: 4,
  rolling_sharpe: 4,
  rolling_volatility_annualized: 4,
  component_var_by_sector: 4,
  performance_during_index_worst: 4,
  sector_decomposition: 4,
  risk_statistics: 4,
  return_statistics: 4,
};

export function clampColSpan(colSpan: number): TearsheetLayoutItem['colSpan'] {
  return ALLOWED_COL_SPANS.has(colSpan as TearsheetLayoutItem['colSpan']) ? (colSpan as TearsheetLayoutItem['colSpan']) : 6;
}

/** Snaps a stored, dropped, or duplicated width to the nearest width the stencil can render readably. */
export function clampColSpanFor(id: string, colSpan: number): TearsheetLayoutItem['colSpan'] {
  const span = clampColSpan(colSpan);
  const allowed = allowedColSpans(id);
  if (allowed.includes(span)) return span;
  return allowed.reduce((best, option) => (Math.abs(option - span) < Math.abs(best - span) ? option : best), allowed[0]!);
}

export function clampRowSpan(rowSpan: number): RowSpanUnits {
  if (ALLOWED_ROW_SPANS.has(rowSpan as RowSpanUnits)) return rowSpan as RowSpanUnits;
  if (rowSpan <= 1) return 1;
  if (rowSpan <= 2) return 2;
  if (rowSpan <= 3) return 3;
  if (rowSpan <= 4) return 4;
  return 6;
}

function gridUnits(colSpan: number): number {
  return clampColSpan(colSpan) / 3;
}

export function defaultRowSpan(id: string): RowSpanUnits {
  if (isCustomSection(id)) return 2;
  return SECTION_ROW_SPANS[baseSectionId(id)] ?? 2;
}

/** A rectangle on the builder grid. `colStart` / `rowStart` are 1-based, matching CSS grid lines. */
type GridRect = { colStart: number; rowStart: number; colUnits: number; rowUnits: number };

export type PlacedItem = GridRect & { item: TearsheetLayoutItem };

type DropCell = { key: string; column: number; row: number };

/** `header` / `kpis` render as report chrome above the grid — they are never placed on it. */
export function isChromeSection(id: string): boolean {
  return id === 'header' || id === 'kpis';
}

/** Text and the portfolio manager grow with their copy instead of scaling the type down. */
export function growsWithContent(id: string): boolean {
  if (isTextCard(id)) return true;
  return baseSectionId(id) === 'portfolio_manager';
}

/** Left / center / right only changes running text. Tables, charts, and key figures ignore it. */
export function supportsTextAlign(id: string): boolean {
  return growsWithContent(id);
}

function itemUnits(item: TearsheetLayoutItem): { colUnits: number; rowUnits: number } {
  const rowUnits = growsWithContent(item.id) ? clampContentRowSpan(item.id, item.rowSpan ?? defaultRowSpan(item.id)) : defaultRowSpan(item.id);
  return { colUnits: gridUnits(item.colSpan), rowUnits };
}

function hasPosition(item: TearsheetLayoutItem): boolean {
  return typeof item.colStart === 'number' && typeof item.rowStart === 'number';
}

function overlaps(a: GridRect, b: GridRect): boolean {
  return (
    a.colStart < b.colStart + b.colUnits &&
    b.colStart < a.colStart + a.colUnits &&
    a.rowStart < b.rowStart + b.rowUnits &&
    b.rowStart < a.rowStart + a.rowUnits
  );
}

function fitsColumns(rect: GridRect): boolean {
  return rect.colStart >= 1 && rect.colStart + rect.colUnits - 1 <= GRID_COLUMNS;
}

/** Nudges a drop origin left so the item fits in the 4 columns. Widths never exceed the grid, so this always fits. */
export function resolveColStart(colStart: number, colUnits: number): number {
  return Math.min(Math.max(1, colStart), GRID_COLUMNS - colUnits + 1);
}

function rectOf(item: TearsheetLayoutItem): GridRect {
  const units = itemUnits(item);
  return { colStart: resolveColStart(item.colStart ?? 1, units.colUnits), rowStart: Math.max(1, item.rowStart ?? 1), ...units };
}

/** First origin (row-major from `fromRow`) where `colUnits` × `rowUnits` fits without overlapping `settled`. */
function firstFreeRect(settled: GridRect[], colUnits: number, rowUnits: number, fromRow: number): GridRect {
  for (let rowStart = Math.max(1, fromRow); ; rowStart += 1) {
    for (let colStart = 1; colStart <= GRID_COLUMNS - colUnits + 1; colStart += 1) {
      const rect = { colStart, rowStart, colUnits, rowUnits };
      if (!settled.some((other) => overlaps(rect, other))) return rect;
    }
  }
}

/** One-time migration for stored items without a position: left→right, wrapping below the tallest item of the band. */
function bandFlowRects(items: TearsheetLayoutItem[]): Map<string, GridRect> {
  const rects = new Map<string, GridRect>();
  let rowStart = 1;
  let colStart = 1;
  let bandHeight = 0;

  for (const item of items) {
    const { colUnits, rowUnits } = itemUnits(item);
    if (colStart > 1 && colStart + colUnits - 1 > GRID_COLUMNS) {
      rowStart += Math.max(bandHeight, 1);
      colStart = 1;
      bandHeight = 0;
    }
    rects.set(item.id, { colStart, rowStart, colUnits, rowUnits });
    colStart += colUnits;
    bandHeight = Math.max(bandHeight, rowUnits);
    if (colStart > GRID_COLUMNS) {
      rowStart += Math.max(bandHeight, 1);
      colStart = 1;
      bandHeight = 0;
    }
  }

  return rects;
}

/** Keeps stored positions (clamped into 4 columns) and gives anything unpositioned the first free origin. */
function settledRects(items: TearsheetLayoutItem[]): Map<string, GridRect> {
  const settled: GridRect[] = [];
  const rects = new Map<string, GridRect>();
  const claim = (item: TearsheetLayoutItem, wanted: GridRect) => {
    const rect = settled.some((other) => overlaps(wanted, other))
      ? firstFreeRect(settled, wanted.colUnits, wanted.rowUnits, wanted.rowStart)
      : wanted;
    settled.push(rect);
    rects.set(item.id, rect);
  };

  for (const item of items.filter(hasPosition)) claim(item, rectOf(item));
  for (const item of items.filter((entry) => !hasPosition(entry))) {
    const { colUnits, rowUnits } = itemUnits(item);
    claim(item, firstFreeRect(settled, colUnits, rowUnits, 1));
  }

  return rects;
}

/** Gives every grid section an explicit, non-overlapping `colStart` / `rowStart`. Idempotent once items are positioned. */
export function normalizePositions(items: TearsheetLayoutItem[]): TearsheetLayoutItem[] {
  const normalizedSpans = items.map<TearsheetLayoutItem>((item) => {
    if (baseSectionId(item.id) === 'portfolio_manager' && item.colSpan !== 12) {
      return { ...item, colSpan: 12 };
    }
    return item;
  });
  const grid = normalizedSpans.filter((item) => !isChromeSection(item.id));
  if (!grid.length) return normalizedSpans;
  const rects = grid.some(hasPosition) ? settledRects(grid) : bandFlowRects(grid);
  return normalizedSpans.map((item) => {
    const rect = rects.get(item.id);
    if (!rect || (item.colStart === rect.colStart && item.rowStart === rect.rowStart)) return item;
    return { ...item, colStart: rect.colStart, rowStart: rect.rowStart };
  });
}

/** Render placements for the grid sections. Call {@link normalizePositions} first for items coming from storage. */
export function placeItems(items: TearsheetLayoutItem[]): PlacedItem[] {
  return items.filter((item) => !isChromeSection(item.id)).map((item) => ({ item, ...rectOf(item) }));
}

/** Row tracks the document needs; the builder asks for one extra empty row to drop into. */
export function gridRowCount(placed: PlacedItem[], opts?: { trailingEmptyRow?: boolean; extraRows?: number }): number {
  const occupied = maxOccupiedRow(placed);
  const extra = opts?.extraRows ?? 0;
  return Math.max(occupied + extra + (opts?.trailingEmptyRow ? 1 : 0), 1);
}

function maxOccupiedRow(placed: PlacedItem[]): number {
  return placed.reduce((max, cell) => Math.max(max, cell.rowStart + cell.rowUnits - 1), 0);
}

/**
 * Layout px of one A4 portrait sheet. The PDF render lays the report out at {@link PRINT_LAYOUT_WIDTH_PX}
 * and scales that onto the 210mm sheet width, so a 297mm sheet is that same width × 297/210.
 */
export const A4_PAGE_HEIGHT_PX = Math.floor((PRINT_LAYOUT_WIDTH_PX * 297) / 210);

/** Whole grid rows that fit on one A4 sheet after `reservedPx` (report padding, first-page chrome). */
export function a4RowsPerPage(reservedPx = 0): number {
  const usable = A4_PAGE_HEIGHT_PX - reservedPx;
  return Math.max(1, Math.floor((usable + GRID_GAP_PX) / (GRID_ROW_PX + GRID_GAP_PX)));
}

/** One printed sheet's worth of grid sections, with `rowStart` rebased so the page's own grid starts at row 1. */
export type GridPage = { items: PlacedItem[]; rows: number };

/** True when any section covers the boundary between `row` and `row + 1`. */
function straddlesRowBoundary(placed: PlacedItem[], row: number): boolean {
  return placed.some((cell) => cell.rowStart <= row && cell.rowStart + cell.rowUnits - 1 > row);
}

/** Last row at or before `ideal` that no section straddles; falls forward when a section is taller than the budget. */
function pageCutRow(placed: PlacedItem[], pageStart: number, ideal: number, lastRow: number): number {
  for (let row = ideal; row >= pageStart; row -= 1) {
    if (!straddlesRowBoundary(placed, row)) return row;
  }
  // A stencil taller than the page budget (a long portfolio_manager card): keep it whole on an
  // over-tall page rather than shrinking the layout. The reader still breaks somewhere inside it.
  for (let row = ideal + 1; row < lastRow; row += 1) {
    if (!straddlesRowBoundary(placed, row)) return row;
  }
  return lastRow;
}

/**
 * Splits placed sections into printed pages, cutting only at grid-row boundaries where no card straddles
 * the cut. A card that would be split moves whole to the next page and leaves whitespace behind, which is
 * why the frontend — not Chromium's fragmentation of a positioned grid — decides where pages end.
 */
export function paginateGridRows(placed: PlacedItem[], rowsPerPage: number, firstPageRows = rowsPerPage): GridPage[] {
  const lastRow = maxOccupiedRow(placed);
  if (lastRow < 1) return [];
  const pages: GridPage[] = [];
  let pageStart = 1;
  while (pageStart <= lastRow) {
    const budget = Math.max(1, Math.floor(pages.length === 0 ? firstPageRows : rowsPerPage));
    const cut = pageCutRow(placed, pageStart, Math.min(pageStart + budget - 1, lastRow), lastRow);
    pages.push({
      rows: cut - pageStart + 1,
      items: placed
        .filter((cell) => cell.rowStart >= pageStart && cell.rowStart <= cut)
        .map((cell) => ({ ...cell, rowStart: cell.rowStart - pageStart + 1 })),
    });
    pageStart = cut + 1;
  }
  return pages;
}

/** True when no section occupies grid row `row` (1-based). */
function isGridRowEmpty(placed: PlacedItem[], row: number): boolean {
  return !placed.some((cell) => row >= cell.rowStart && row < cell.rowStart + cell.rowUnits);
}

/** Rows that may be removed (fully empty, including the last empty drop/padding row). */
export function deletableEmptyGridRows(placed: PlacedItem[], rowCount: number): number[] {
  const rows: number[] = [];
  for (let row = 1; row <= rowCount; row += 1) {
    if (isGridRowEmpty(placed, row)) rows.push(row);
  }
  return rows;
}

function shiftRowStartsAfter(items: TearsheetLayoutItem[], afterRow: number, delta: number): TearsheetLayoutItem[] {
  return items.map((item) => {
    if (isChromeSection(item.id)) return item;
    const start = item.rowStart ?? 1;
    if (start <= afterRow) return item;
    return { ...item, rowStart: Math.max(1, start + delta) };
  });
}

/** Remove a fully empty row; no-op when the row is occupied. */
export function deleteEmptyGridRow(
  items: TearsheetLayoutItem[],
  row: number,
  extraRows = 0,
): { layout: TearsheetLayoutItem[]; extraRows: number } | null {
  const layout = normalizePositions(items);
  const placed = placeItems(layout);
  const rowCount = gridRowCount(placed, { trailingEmptyRow: false, extraRows });
  if (!deletableEmptyGridRows(placed, rowCount).includes(row)) return null;
  const occupied = maxOccupiedRow(placed);
  if (row > occupied) {
    return { layout, extraRows: Math.max(0, extraRows - 1) };
  }
  return { layout: normalizePositions(shiftRowStartsAfter(layout, row, -1)), extraRows };
}

/** Insert a blank row after `afterRow` (shifts sections down, or adds bottom padding). */
export function insertGridRowAfter(
  items: TearsheetLayoutItem[],
  afterRow: number,
  extraRows = 0,
): { layout: TearsheetLayoutItem[]; extraRows: number } {
  const layout = normalizePositions(items);
  const placed = placeItems(layout);
  const occupied = maxOccupiedRow(placed);
  if (afterRow >= occupied) {
    return { layout, extraRows: extraRows + 1 };
  }
  return { layout: normalizePositions(shiftRowStartsAfter(layout, afterRow, 1)), extraRows };
}

/** Full-cell drop targets for every cell of the grid, including the trailing empty row. */
export function dropCells(rowCount: number): DropCell[] {
  const cells: DropCell[] = [];
  for (let row = 1; row <= rowCount; row += 1) {
    for (let column = 1; column <= GRID_COLUMNS; column += 1) {
      cells.push({ key: `cell-${row}-${column}`, row, column });
    }
  }
  return cells;
}

/**
 * Puts `id` at the given origin and resolves the fallout deterministically:
 * overlapped sections move to the right of the dragged rectangle when they still fit,
 * otherwise to the first free origin below their own row. Sections that do not overlap stay put.
 */
function resolveAt(items: TearsheetLayoutItem[], id: string, rowStart: number, colStart: number): TearsheetLayoutItem[] {
  const normalized = normalizePositions(items);
  const target = normalized.find((item) => item.id === id);
  if (!target || isChromeSection(id)) return normalized;

  const units = itemUnits(target);
  const dragged: GridRect = { colStart: resolveColStart(colStart, units.colUnits), rowStart: Math.max(1, rowStart), ...units };
  const settled: GridRect[] = [dragged];
  const resolved = new Map<string, GridRect>([[id, dragged]]);
  const pushed: Array<{ id: string; rect: GridRect }> = [];

  for (const item of normalized) {
    if (item.id === id || isChromeSection(item.id)) continue;
    const rect = rectOf(item);
    if (overlaps(rect, dragged)) pushed.push({ id: item.id, rect });
    else settled.push(rect);
  }

  pushed.sort((a, b) => a.rect.rowStart - b.rect.rowStart || a.rect.colStart - b.rect.colStart);
  for (const { id: pushedId, rect } of pushed) {
    const toRight = { ...rect, colStart: dragged.colStart + dragged.colUnits };
    const next =
      fitsColumns(toRight) && !settled.some((other) => overlaps(toRight, other))
        ? toRight
        : firstFreeRect(settled, rect.colUnits, rect.rowUnits, rect.rowStart + 1);
    settled.push(next);
    resolved.set(pushedId, next);
  }

  return normalized.map((item) => {
    const rect = resolved.get(item.id);
    if (!rect || (item.colStart === rect.colStart && item.rowStart === rect.rowStart)) return item;
    return { ...item, colStart: rect.colStart, rowStart: rect.rowStart };
  });
}

/** Drop handler: the drop cell becomes the section's top-left cell. */
export function moveItemToCell(items: TearsheetLayoutItem[], id: string, rowStart: number, colStart: number): TearsheetLayoutItem[] {
  return resolveAt(items, id, rowStart, colStart);
}

/** Width change: keeps the origin row, nudges left when the new width would overflow, then resolves collisions. */
export function resizeItem(items: TearsheetLayoutItem[], id: string, colSpan: number): TearsheetLayoutItem[] {
  const clamped = clampColSpanFor(id, colSpan);
  const resized = items.map((item) => (item.id === id ? { ...item, colSpan: clamped } : item));
  const target = resized.find((item) => item.id === id);
  if (!target) return items;
  return resolveAt(resized, id, target.rowStart ?? 1, target.colStart ?? 1);
}

/** Origin for a section being added or duplicated: first free cell, row-major. */
export function firstFreePosition(items: TearsheetLayoutItem[], sectionId: string, colSpan: number): { colStart: number; rowStart: number } {
  const rect = firstFreeRect(placeItems(normalizePositions(items)), gridUnits(colSpan), defaultRowSpan(sectionId), 1);
  return { colStart: rect.colStart, rowStart: rect.rowStart };
}

export function defaultColSpan(id: string): TearsheetLayoutItem['colSpan'] {
  const base = baseSectionId(id);
  const preferred =
    base === 'header' ||
    base === 'kpis' ||
    base === 'monthly' ||
    base === 'commentary' ||
    base === 'sector_attribution' ||
    base === 'portfolio_manager' ||
    base === 'disclaimer' ||
    base === 'predefined_text'
      ? 12
      : 6;
  return clampColSpanFor(id, preferred);
}

export function widthValue(colSpan: number): string {
  return String(clampColSpan(colSpan));
}

export function spanFromCol(colSpan: number): TearsheetCustomBox['span'] {
  const span = clampColSpan(colSpan);
  if (span >= 12) return 'full';
  if (span >= 9) return 'wide';
  if (span >= 6) return 'normal';
  return 'compact';
}

export function sectionLabel(id: string, customBoxes: TearsheetCustomBox[]): string {
  const custom = customBoxes.find((box) => box.id === id);
  if (custom) return custom.title;
  return SECTION_LABELS[baseSectionId(id)] ?? id;
}

export function baseSectionId(id: string): string {
  if (CANONICAL_SET.has(id)) return id;
  let candidate = id;
  while (true) {
    const copyMatch = candidate.match(/^(.+)_copy_[a-z0-9]+$/i);
    if (!copyMatch) break;
    const parent = copyMatch[1]!;
    if (CANONICAL_SET.has(parent)) return parent;
    candidate = parent;
  }
  return id;
}

export function isCustomSection(id: string): boolean {
  return id.startsWith('custom_');
}

export function colClass(colSpan: number): string {
  const span = clampColSpan(colSpan);
  if (span >= 12) return 'col-12';
  if (span >= 9) return 'col-9';
  if (span >= 6) return 'col-6';
  return 'col-3';
}

export function rowClass(rowSpan: number): string {
  return `row-${clampRowSpan(rowSpan)}`;
}
